import { createContext, useState, useContext, useEffect } from 'react';
import api, { setIntegrityKey } from '../api';

const AuthContext = createContext(null);

// Build Discord avatar URL from hash; normalize API user for nav (username, avatarUrl, discord_id)
function normalizeUser(apiUser) {
  if (!apiUser) return null;
  
  // Build Discord avatar URL - handle null avatar (default Discord avatar)
  let avatarUrl = null;
  if (apiUser.avatar && apiUser.discord_id) {
    avatarUrl = `https://cdn.discordapp.com/avatars/${apiUser.discord_id}/${apiUser.avatar}.png`;
  } else if (apiUser.discord_id) {
    // Use default Discord avatar if no custom avatar
    avatarUrl = `https://cdn.discordapp.com/embed/avatars/${(parseInt(apiUser.discord_id) >> 22) % 6}.png`;
  }
  
  return {
    ...apiUser,
    username: apiUser.username || null,
    discord_id: apiUser.discord_id || null,
    avatarUrl
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [banInfo, setBanInfo] = useState(null);
  const [hasLicense, setHasLicense] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminPerms, setAdminPerms] = useState(null);
  const [adminRole, setAdminRole] = useState(null);
  const [hasEnterprise, setHasEnterprise] = useState(false);
  const [isEnterpriseMember, setIsEnterpriseMember] = useState(false);
  const [enterpriseName, setEnterpriseName] = useState(null);
  const [licenseGame, setLicenseGame] = useState(null); // 'fivem' | 'minecraft' | null
  const [hasGVSecurityPlus, setHasGVSecurityPlus] = useState(false);
  const [discordRoles, setDiscordRoles] = useState([]);
  const [rolesReady, setRolesReady] = useState(false);

  // Computed: user has dashboard access if they have license, enterprise, or are admin
  const hasDashboardAccess = !!(hasLicense || hasEnterprise || isEnterpriseMember || isAdmin);

  // OPTIMIZATION: Use localStorage cache for display-only fields + /auth/init for instant verification
  // Privilege flags (isAdmin, has_license) are NEVER cached — always fetched from server
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Only cache non-privilege display fields (username, avatar, discord_id)
      const cached = localStorage.getItem('auth_cache');
      if (cached) {
        try {
          const cache = JSON.parse(cached);
          setUser(normalizeUser(cache.user));
          // DO NOT restore isAdmin, has_license, permissions from cache — security risk
        } catch (_) { }
      }

      // Lightweight endpoint for instant user verification (no DB queries)
      api.get('/auth/init')
        .then(res => {
          setUser(normalizeUser(res.data?.user));
        })
        .catch(err => {
          if (err.response?.status === 403 && err.response?.data?.error === 'banned') {
            setBanInfo(err.response.data.ban || {
              reason: err.response.data.reason || 'No reason provided',
              duration: err.response.data.duration || 'Lifetime',
              expires_at: err.response.data.expiresAt,
              banned_at: err.response.data.banned_at,
              banned_by: err.response.data.banned_by
            });
            setUser(null);
            setHasLicense(false);
            localStorage.removeItem('token');
            setLoading(false);
          } else {
            setUser(null);
            setHasLicense(false);
            localStorage.removeItem('token');
            setLoading(false);
          }
        });

      // Reset integrity key before fetching new session key
      setIntegrityKey(null);

      // Full auth load in background (non-blocking for tickets)
      api.get('/auth/me')
        .then(res => {
          const raw = res.data?.user ?? res.data;
          setUser(normalizeUser(raw));
          setHasLicense(!!(res.data?.has_license ?? res.data?.license));
          setIsAdmin(!!res.data?.isAdmin);
          setAdminPerms(res.data?.permissions ?? null);
          setAdminRole(res.data?.adminRole ?? null);
          if (res.data?.hasGVSecurityPlus !== undefined) setHasGVSecurityPlus(!!res.data.hasGVSecurityPlus);
          if (res.data?._ik) setIntegrityKey(res.data._ik);

          // Cache only non-privilege display fields for next visit
          localStorage.setItem('auth_cache', JSON.stringify({
            user: { discord_id: raw?.discord_id, username: raw?.username, avatar: raw?.avatar },
            cached_at: Date.now()
          }));

          setBanInfo(null);
        })
        .catch(err => {
          if (err.response?.status === 403 && err.response?.data?.error === 'banned') {
            setBanInfo(err.response.data.ban || {
              reason: err.response.data.reason || 'No reason provided',
              duration: err.response.data.duration || 'Lifetime',
              expires_at: err.response.data.expiresAt,
              banned_at: err.response.data.banned_at,
              banned_by: err.response.data.banned_by
            });
            setUser(null);
            setHasLicense(false);
            localStorage.removeItem('token');
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || loading) return;
    Promise.all([
      api.get('/settings').catch(() => ({ data: {} })),
      api.get('/enterprise/check').catch(() => ({ data: { hasEnterprise: false } }))
    ]).then(([settingsRes, enterpriseRes]) => {
      const license = settingsRes.data?.has_license ?? settingsRes.data?.license;
      if (license !== undefined) setHasLicense(!!license);
      setHasEnterprise(!!enterpriseRes.data?.hasEnterprise);
      setIsEnterpriseMember(!!settingsRes.data?.isEnterpriseMember);
      setEnterpriseName(settingsRes.data?.enterpriseName || null);
      const game = settingsRes.data?.license?.scan_type ?? null;
      if (game === 'fivem' || game === 'minecraft') setLicenseGame(game);
      if (settingsRes.data?.hasGVSecurityPlus !== undefined) setHasGVSecurityPlus(!!settingsRes.data.hasGVSecurityPlus);
      if (Array.isArray(settingsRes.data?.discord_roles)) setDiscordRoles(settingsRes.data.discord_roles);
      setRolesReady(true);
    });
  }, [user, loading]);

  // Refetch auth when server rejects with NO_LICENSE (corrects bypassed frontend state)
  useEffect(() => {
    const handler = () => {
      setHasLicense(false);
      api.get('/auth/me').then(res => {
        setHasLicense(!!(res.data?.has_license ?? res.data?.license));
      }).catch(() => setHasLicense(false));
    };
    window.addEventListener('license-required', handler);
    return () => window.removeEventListener('license-required', handler);
  }, []);

  const loginWithDiscord = async () => {
    window.location.href = '/api/auth/discord';
  };

  const handleAuthCallback = (token) => {
    localStorage.setItem('token', token);
    return api.get('/auth/me').then(res => {
      const data = res.data;
      if (data.ban) {
        setBanInfo(data.ban);
        setUser(null);
        setHasLicense(false);
        localStorage.removeItem('token');
        return null;
      }
      const raw = data.user ?? data;
      const normalized = normalizeUser(raw);
      setUser(normalized);
      setHasLicense(!!(data.has_license ?? data.license));
      setIsAdmin(!!data.isAdmin);
      setAdminPerms(data.permissions ?? null);
      setAdminRole(data.adminRole ?? null);
      setBanInfo(null);
      return normalized;
    }).catch(err => {
      if (err.response?.status === 403 && err.response?.data?.error === 'banned') {
        setBanInfo(err.response.data.ban || {
          reason: err.response.data.reason || 'No reason provided',
          duration: err.response.data.duration || 'Lifetime',
          expires_at: err.response.data.expiresAt,
          banned_at: err.response.data.banned_at,
          banned_by: err.response.data.banned_by
        });
        setUser(null);
        localStorage.removeItem('token');
        return null;
      }
      throw err;
    });
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setBanInfo(null);
    setHasLicense(false);
    setIsAdmin(false);
    setAdminPerms(null);
    setAdminRole(null);
    setHasEnterprise(false);
    setIsEnterpriseMember(false);
    setEnterpriseName(null);
    setHasGVSecurityPlus(false);
    setDiscordRoles([]);
    setRolesReady(false);
  };

  const setBanFromUrl = (banInfoParam) => {
    try {
      const obj = JSON.parse(decodeURIComponent(banInfoParam));
      if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return;
      setBanInfo({
        reason: typeof obj.reason === 'string' ? obj.reason.slice(0, 500) : null,
        banned_at: typeof obj.banned_at === 'string' ? obj.banned_at : null,
        expires_at: typeof obj.expires_at === 'string' ? obj.expires_at : null,
        is_permanent: typeof obj.is_permanent === 'boolean' ? obj.is_permanent : (typeof obj.is_permanent === 'number' ? !!obj.is_permanent : true),
        banned_by: typeof obj.banned_by === 'string' ? obj.banned_by.slice(0, 100) : null,
      });
    } catch (_) {}
  };

  // isCEO must come from the server verify response; client-side role ID checks are insecure.
  const isCEO = false;

  return (
    <AuthContext.Provider value={{
      user,
      loginWithDiscord,
      handleAuthCallback,
      logout,
      loading,
      banInfo,
      hasLicense,
      isAdmin,
      adminPerms,
      adminRole,
      hasEnterprise,
      isEnterpriseMember,
      enterpriseName,
      licenseGame,
      hasGVSecurityPlus,
      discordRoles,
      rolesReady,
      isCEO,
      hasDashboardAccess,
      setBanFromUrl
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
